import { isEqual } from "lodash";
import { createContext, Dispatch, useContext, useEffect, useReducer } from "react";
import { TypeAheadItem } from "../controls";
import { figureReducer } from "./functions/figureReducer";
import {
  BeFigureAction,
  BeFigureState,
  FIGURE_CHANGE_SENT,
  LOAD_FIGURE_INPUT,
} from "./functions/interface";
import { FigureDataDto, FigureSettingsDto } from "./types";

interface BeFigureContextProps {
  state: BeFigureState;
  dispatch: Dispatch<BeFigureAction>;
}

export const BeFigureContext = createContext<BeFigureContextProps | null>(null);

interface BeFigureContextProviderProps {
  children: React.ReactNode;
  figureInput: FigureDataDto;
  figureSettings: FigureSettingsDto;
  updateFigureInput: (ret: FigureDataDto) => void;
  varNames: TypeAheadItem[];
}

/**
 * Provider that supplies figure state and dispatch to child components.
 *
 * @param props - Component props
 * @param props.children - Child nodes wrapped by the provider
 * @param props.figureInput - Figure data to render
 * @param props.figureSettings - Options for figure types
 * @param props.updateFigureInput - Function to partially update the figure data
 * @param props.varNames - List of variable names to choose from
 * @returns BeFigureContextProvider component
 */
export const BeFigureContextProvider = ({
  children,
  figureInput,
  figureSettings,
  updateFigureInput,
  varNames,
}: BeFigureContextProviderProps) => {
  const [state, dispatch] = useReducer(figureReducer, {
    figureInput,
    figureSettings,
    varNames,
    changed: false,
  });

  useEffect(() => {
    const eq = isEqual(state.figureInput, figureInput);
    // Send changes up
    if (!eq && state.figureInput.id === figureInput.id && state.changed) {
      updateFigureInput(state.figureInput);
      dispatch({ operation: FIGURE_CHANGE_SENT });
    }
    // Accept new input if it differs from current state and wasn't just sent
    else if (state.figureInput.id !== figureInput.id || !eq) {
      dispatch({ operation: LOAD_FIGURE_INPUT, figureInput });
    }
  }, [figureInput, state.changed, state.figureInput, updateFigureInput]);

  return (
    <BeFigureContext.Provider value={{ state, dispatch }}>{children}</BeFigureContext.Provider>
  );
};

BeFigureContextProvider.displayName = "BeFigureContextProvider";

/**
 * Custom hook to access the BeFigure context.
 *
 * @returns The figure context object
 */
export const useBeFigure = () => {
  const context = useContext(BeFigureContext);
  return !context
    ? {
        state: null,
        dispatch: () => console.error("WARNING: Dispatch not initiated"),
      }
    : context;
};
